/**
 * The API client.
 *
 * Every backend response is `{ success, data, message }` (or `{ success:false, error }`),
 * so this unwraps the envelope once and everything above it deals in plain data.
 * A 401 triggers one refresh attempt, and concurrent 401s share that single refresh
 * rather than each firing their own.
 */

const BASE = '/api/v1';

// Sys keeps its own session. It lives on its own origin already, but its own
// keys mean it would stay separate from the admin's even if the two ever
// shared one — a sys token is not an admin token and must never be read as one.
const ACCESS_KEY = 'viganium.sys.accessToken';
const REFRESH_KEY = 'viganium.sys.refreshToken';

export interface ApiErrorShape {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  constructor(status: number, error: ApiErrorShape) {
    super(error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = error.code;
    this.details = error.details;
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshInFlight: Promise<boolean> | null = null;

async function refreshTokens(): Promise<boolean> {
  const refreshToken = tokens.refresh;
  if (!refreshToken) return false;

  // Collapse a burst of 401s into one refresh call.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return false;
      const body = await res.json();
      tokens.set(body.data.accessToken, body.data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Let the next 401 start a fresh attempt.
      setTimeout(() => (refreshInFlight = null), 0);
    }
  })();

  return refreshInFlight;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Internal: prevents an infinite refresh loop. */
  _retried?: boolean;
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, params, signal } = options;

  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return request<T>(path, { ...options, _retried: true });
    tokens.clear();
    window.dispatchEvent(new CustomEvent('viganium:signed-out'));
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has expired.' });
  }

  if (res.status === 204) return undefined as T;

  const payload = await res.json().catch(() => null);

  if (!res.ok) {
    const error: ApiErrorShape = payload?.error ?? {
      code: 'NETWORK_ERROR',
      message: `Request failed (${res.status}).`,
    };
    if (res.status === 401) {
      tokens.clear();
      window.dispatchEvent(new CustomEvent('viganium:signed-out'));
    }
    throw new ApiError(res.status, error);
  }

  return payload?.data as T;
}

/** POST/PATCH responses may carry a toast-worthy message alongside the data. */
export async function requestWithMessage<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<{ data: T; message: string | null }> {
  const { method = 'GET', body, params } = options;
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (res.status === 401 && !options._retried && tokens.refresh) {
    if (await refreshTokens()) return requestWithMessage<T>(path, { ...options, _retried: true });
  }

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: 'Request failed.' });
  }
  return { data: payload?.data as T, message: payload?.message ?? null };
}

interface UploadOptions {
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/** `POST /files/uploads` — the file is checked, and comes back with a ticket and where its bytes go. */
interface UploadStart {
  ticket: string;
  upload: { method: 'PUT'; url: string; headers: Record<string, string> };
}

/**
 * Uploads one file — a client's logo — reporting progress as it goes, and
 * returns it to be saved by id with the form it belongs to. The same three
 * steps as the admin's uploads (apps/admin/src/lib/api.ts): the API checks the
 * file and says where its bytes go — straight into Cloudflare R2 through a link
 * it signed, or to the API itself when files are kept on the server — the bytes
 * go there, and the API keeps the file. An API from before this offers only the
 * one-step upload, which is used then.
 */
export async function uploadFile(blob: Blob, name: string, options: UploadOptions = {}): Promise<UploadedFile> {
  let start: UploadStart;
  try {
    start = await request<UploadStart>('/files/uploads', {
      method: 'POST',
      body: { name, type: blob.type, size: blob.size },
      signal: options.signal,
    });
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return uploadWhole(blob, name, options);
    throw e;
  }
  await sendBytes(blob, start.upload, options);
  return request<UploadedFile>('/files/uploads/complete', {
    method: 'POST',
    body: { ticket: start.ticket },
    signal: options.signal,
  });
}

/** The bytes, to where the API said — XMLHttpRequest, since `fetch` cannot report an upload's progress. */
function sendBytes(blob: Blob, target: UploadStart['upload'], options: UploadOptions): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (options.signal?.aborted) {
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
      return;
    }
    // R2 is another origin; the API's own route is this one.
    const toStorage = new URL(target.url, window.location.origin).origin !== window.location.origin;
    const xhr = new XMLHttpRequest();
    xhr.open(target.method, target.url);
    // Exactly these: an R2 link is signed with them.
    for (const [header, value] of Object.entries(target.headers)) xhr.setRequestHeader(header, value);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const settle = () => options.signal?.removeEventListener('abort', abort);

    xhr.onload = () => {
      settle();
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* R2 answers in XML, a proxy in HTML — neither is our envelope */
      }
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : toStorage
            ? { code: 'FILE_STORAGE_REFUSED', message: `The file storage refused the upload (${xhr.status}).` }
            : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    // A storage that does not let this page upload (its CORS policy) looks the same as no network.
    xhr.onerror = () => {
      settle();
      reject(
        new ApiError(
          0,
          toStorage
            ? { code: 'FILE_STORAGE_UNREACHABLE', message: 'The file storage could not be reached.' }
            : { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' },
        ),
      );
    };
    xhr.onabort = () => {
      settle();
      reject(new DOMException('The upload was cancelled.', 'AbortError'));
    };
    xhr.send(blob);
  });
}

/**
 * The one-step upload, `POST /files/upload`: the whole file to the API in one
 * request. Only for an API that has no three-step upload yet.
 */
function uploadWhole(blob: Blob, name: string, options: UploadOptions = {}, retried = false): Promise<UploadedFile> {
  return new Promise<UploadedFile>((resolve, reject) => {
    const form = new FormData();
    form.append('file', blob, name);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${BASE}/files/upload`);
    const access = tokens.access;
    if (access) xhr.setRequestHeader('Authorization', `Bearer ${access}`);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) options.onProgress?.(e.loaded / e.total);
    };
    const abort = () => xhr.abort();
    options.signal?.addEventListener('abort', abort, { once: true });

    xhr.onload = async () => {
      options.signal?.removeEventListener('abort', abort);
      if (xhr.status === 401 && !retried && tokens.refresh && (await refreshTokens())) {
        uploadWhole(blob, name, options, true).then(resolve, reject);
        return;
      }
      let payload: any = null;
      try {
        payload = JSON.parse(xhr.responseText);
      } catch {
        /* the proxy's own error page, not our envelope */
      }
      if (xhr.status >= 200 && xhr.status < 300 && payload?.data) return resolve(payload.data as UploadedFile);
      // A 413 from the proxy in front of the API arrives as HTML, not as our error.
      const error: ApiErrorShape =
        payload?.error ??
        (xhr.status === 413
          ? { code: 'FILE_TOO_LARGE', message: 'The file is too large.' }
          : { code: 'NETWORK_ERROR', message: `Upload failed (${xhr.status}).` });
      reject(new ApiError(xhr.status, error));
    };
    xhr.onerror = () => reject(new ApiError(0, { code: 'NETWORK_ERROR', message: 'The upload was interrupted.' }));
    xhr.onabort = () => reject(new DOMException('The upload was cancelled.', 'AbortError'));
    xhr.send(form);
  });
}

/**
 * Saves a file the API generates — an invoice or a receipt PDF. A plain link
 * cannot carry the session's bearer token, so the file is fetched like any
 * other call (refresh included) and handed to the browser under the name the
 * server gave it.
 */
export async function download(
  path: string,
  params?: RequestOptions['params'],
  retried = false,
): Promise<void> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = {};
  const access = tokens.access;
  if (access) headers.Authorization = `Bearer ${access}`;

  const res = await fetch(url.toString().replace(window.location.origin, ''), { headers });

  if (res.status === 401 && !retried && tokens.refresh) {
    if (await refreshTokens()) return download(path, params, true);
    tokens.clear();
    window.dispatchEvent(new CustomEvent('viganium:signed-out'));
    throw new ApiError(401, { code: 'TOKEN_INVALID', message: 'Your session has expired.' });
  }
  if (!res.ok) {
    const payload = await res.json().catch(() => null);
    throw new ApiError(res.status, payload?.error ?? { code: 'NETWORK_ERROR', message: `Request failed (${res.status}).` });
  }

  const href = URL.createObjectURL(await res.blob());
  const link = document.createElement('a');
  link.href = href;
  link.download = fileNameFrom(res.headers.get('Content-Disposition')) ?? 'download';
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(href), 10_000);
}

/** RFC 6266: the UTF-8 `filename*` when there is one, the plain `filename` otherwise. */
function fileNameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // A malformed escape: fall back to the plain name.
    }
  }
  return /filename="([^"]+)"/i.exec(disposition)?.[1] ?? null;
}

export const api = {
  get: <T>(path: string, params?: RequestOptions['params']) => request<T>(path, { params }),
  post: <T>(path: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'POST', body, params }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => request<T>(path, { method: 'DELETE', body }),
};

// ---------------------------------------------------------------------------
// Shared response shapes
// ---------------------------------------------------------------------------

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface UserRef {
  id: string;
  fullName: string;
  fullNameAr?: string | null;
  email?: string;
  jobTitle?: string | null;
}

/** A file the API keeps, as an upload returns it — and as a payment's transfer receipt comes back. */
export interface UploadedFile {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
  /** Shows it in the page. Works without a session. */
  url: string;
  /** Saves it. */
  downloadUrl: string;
}

/** `GET /files/policy` — what an upload may be, before anything is sent. */
export interface FilePolicy {
  /** Pictures, documents and recordings: Settings › Security "Largest upload". */
  maxUploadMb: number;
  maxVideoMb?: number;
  extensions: string[];
}

export interface DepartmentRef {
  id: string;
  name: string;
  nameAr?: string | null;
  code: string;
}

export interface ProjectRef {
  id: string;
  name: string;
  nameAr?: string | null;
  code: string;
  status?: string;
}

export type AppSystem = 'ADMIN' | 'SYS';

export interface CurrentUser {
  id: string;
  email: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode: string | null;
  jobTitle: string | null;
  phone: string | null;
  locale: string;
  isActive: boolean;
  lastLoginAt: string | null;
  /** The system this session signed in to — always SYS here. */
  app: AppSystem;
  /** Every system the person can open; the sidebar links to the admin from it. */
  systems: AppSystem[];
  /** Sys roles only: a sys session never carries the admin's. */
  roles: Array<{ code: string; name: string; nameAr: string | null }>;
  permissions: string[];
  departments: Array<DepartmentRef & { memberType: 'MEMBER' | 'LEADER' }>;
}
